import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import TabErrorBanner from '@/components/v2/molecules/admin/shared/TabErrorBanner';
import PropertyOwnersTabSkeleton from '@/components/v2/molecules/admin/tabs/UserManagementTabs/PropertyOwners/PropertyOwnersTabSkeleton';
import { useAuth } from '@/contexts/AuthContext';
import { useUserManagement } from '@/hooks/useUserManagement';
import { useUsers } from '@/hooks/useUsers';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import TabWarningBanner from '../../../../../molecules/admin/shared/TabWarningBanner';
import ManagedUserCard from '../../../../../molecules/admin/tabs/shared/ManagedUserCard';
import CreateUserModal from '../../shared/CreateUserModal';
import AssignPropertiesModal from '../PropertyOwners/AssignPropertiesModal';

const PropertyManagersTab = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedPropertyManager, setSelectedPropertyManager] =
    useState<any>(null);
  const [resendingUserId, setResendingUserId] = useState<string | null>(null);

  const { user, profile } = useAuth();

  const {
    data: PropertyManagers = [],
    isLoading: isLoadingPropertyManagers,
    error: errorPropertyManagers,
    refetch: refetchPropertyManagers,
  } = useUsers('property_manager');

  const { resendInvitation } = useUserManagement();

  const openCreateModal = () => setShowCreateModal(true);
  const closeCreateModal = () => setShowCreateModal(false);

  const openAssignModal = (propertyManager: any) => {
    setSelectedPropertyManager(propertyManager);
    setShowAssignModal(true);
  };

  const closeAssignModal = () => {
    setSelectedPropertyManager(null);
    setShowAssignModal(false);
  };

  const handleResendInvitation = async (userId: string) => {
    setResendingUserId(userId);
    await resendInvitation(userId);
    setResendingUserId(null);
  };

  const handleCreateUserSuccess = async (createdUserId: string) => {
    closeCreateModal();

    const { data: updatedPropertyManagers = [] } =
      await refetchPropertyManagers();
    const newOwner = updatedPropertyManagers.find(
      (owner) => owner.user_id === createdUserId,
    );

    if (newOwner) {
      openAssignModal(newOwner);
    }
  };

  const handleAssignSuccess = () => {
    closeAssignModal();
    refetchPropertyManagers();
  };

  if (!user || !profile || profile.role !== 'super_admin') {
    return (
      <TabWarningBanner
        title="Acceso denegado"
        message="Solo los super administradores pueden acceder a esta sección."
      />
    );
  }

  if (isLoadingPropertyManagers) return <PropertyOwnersTabSkeleton />;

  if (errorPropertyManagers) {
    return (
      <TabErrorBanner
        title="Error al cargar administradores de propiedades"
        message="No se pudieron cargar los administradores de propiedades. Por favor, intenta nuevamente."
        onRetry={() => refetchPropertyManagers()}
      />
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Administradores de Propiedades
          </h2>

          <p className="text-gray-600">
            Gestiona los administradores y sus propiedades asignadas
          </p>
        </div>

        <Button
          className="bg-brand-primary hover:bg-brand-primary-hover flex items-center gap-2"
          onClick={openCreateModal}
        >
          <Plus className="w-4 h-4 mr-2" />
          Crear Administrador
        </Button>
      </div>

      <div className="grid gap-4">
        {PropertyManagers.map((propertyManager) => (
          <ManagedUserCard
            key={propertyManager.id}
            user={propertyManager}
            onAssign={openAssignModal}
            onResendInvitation={handleResendInvitation}
            isLoading={resendingUserId === propertyManager.user_id}
          />
        ))}

        {PropertyManagers.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-gray-600">
                No hay administradores de propiedades registrados
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      <CreateUserModal
        isOpen={showCreateModal}
        onClose={closeCreateModal}
        onSuccess={handleCreateUserSuccess}
        userRole="property_manager"
      />

      {selectedPropertyManager && (
        <AssignPropertiesModal
          isOpen={showAssignModal}
          onClose={closeAssignModal}
          onSuccess={handleAssignSuccess}
          user={selectedPropertyManager}
        />
      )}
    </div>
  );
};

export default PropertyManagersTab;
